import { useLocation, useParams } from 'react-router-dom'
import { Menu, Search, Sparkles } from 'lucide-react'
import { MODULE_CATALOG, isModuleEnabled, isRailModule } from '@os/contracts/shared'
import { MODULE_UI_META } from '../../utils/moduleUiMeta'
import { useAppShellStore } from '../../stores/appShellStore'
import { useServerStore } from '../../shell/stores/serverStore'
import { useAuthStore } from '../../stores/authStore'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import { useActiveServer } from '../../hooks/useActiveServer'
import { useMandatoryLearningStore } from '../../stores/mandatoryLearningStore'
import { useBrandStore } from '../../stores/brandStore'

// Best-effort human title for the current route. Core surfaces first,
// then catalog modules matched by their route prefix (moduleUiMeta).
// `fallback` is the tenant brand's short name (white label §4.1) — the last
// resort when no route and no module matches.
function getContextTitle(pathname: string, fallback: string): string {
  if (pathname.startsWith('/channels/@me')) return 'Chats'
  if (pathname.startsWith('/servers/')) return 'Business OS'
  if (pathname.startsWith('/profile')) return 'Profile'
  if (pathname.startsWith('/admin')) return 'Admin'
  if (pathname.startsWith('/settings')) return 'Settings'
  const mod = MODULE_CATALOG.find((m) => {
    // Skip feature-toggle modules (rail:false, e.g. AI Assistant) — they
    // have no route, so their sentinel match must never win.
    if (!isRailModule(m)) return false
    const meta = MODULE_UI_META[m.key]
    return meta ? pathname.startsWith(meta.match) : false
  })
  return mod?.label ?? fallback
}

export default function MobileTopBar() {
  const location = useLocation()
  const { channelId } = useParams()
  const channelName = useServerStore((s) => {
    if (!channelId) return null
    for (const srv of s.servers) {
      const inServer = srv.channels?.find((c) => c.id === channelId)
      if (inServer) return inServer.name
      const inCat = srv.categories?.flatMap((cat) => cat.channels ?? []).find((c) => c.id === channelId)
      if (inCat) return inCat.name
    }
    return null
  })
  const setMobileNavOpen = useAppShellStore((s) => s.setMobileNavOpen)
  const aiPanelOpen = useAppShellStore((s) => s.aiPanelOpen)
  const toggleAiPanel = useAppShellStore((s) => s.toggleAiPanel)
  // Same active-server + gate logic the rail uses for the footer assistant
  // button (the 'assistant' key; AI Layer is the separate 'ai' module), so
  // the mobile AI button appears under the same conditions.
  const aiEnabled = isModuleEnabled(useActiveServer()?.enabledModules, 'assistant')
  const gated = useMandatoryLearningStore((s) => s.blocked)

  const brandShortName = useBrandStore((s) => s.brand.shortName)

  const currentUserId = useAuthStore((s) => s.user?.id)
  const isDmRoute = location.pathname.startsWith('/channels/') && !location.pathname.startsWith('/channels/@me')
  // DM route (/channels/:id, no serverId) -> show the conversation name (group
  // name, else the peer's username) instead of the generic "Chats".
  const dmName = useDmStore((s) => {
    if (!isDmRoute || !channelId) return null
    const dm = s.dmChannels.find((d) => d.id === channelId)
    if (!dm) return null
    if (dm.name) return dm.name
    const peer = dm.members.find((m) => m.userId !== currentUserId)
    return peer?.user?.username || 'Direct message'
  })

  const title = channelName ?? dmName ?? getContextTitle(location.pathname, brandShortName)

  return (
    <div
      className="shrink-0 flex items-center gap-1 px-2 bg-background border-b border-border"
      // 3rem bar + the status-bar inset. Android WebView (edge-to-edge since
      // targetSdk 35) reports a non-zero env() top inset, and a fixed h-12
      // (border-box) is SMALLER than that inset — the padding collapsed the
      // content box and pushed the buttons up under the status bar. iOS is
      // unaffected: the shell insets natively (contentInset:'always') so
      // env() is 0 there and the bar stays 3rem.
      style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3rem + env(safe-area-inset-top))' }}
    >
      <button
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
        className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition"
      >
        <Menu size={20} />
      </button>
      <span className="flex-1 min-w-0 truncate font-semibold text-foreground px-1">{title}</span>
      <button
        onClick={() => window.dispatchEvent(new Event('global-search:open'))}
        aria-label="Search"
        className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition"
      >
        <Search size={20} />
      </button>
      {aiEnabled && !gated && (
        <button
          onClick={toggleAiPanel}
          aria-label="AI Assistant"
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition ${
            aiPanelOpen
              ? 'text-primary bg-primary/10'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}
        >
          <Sparkles size={20} />
        </button>
      )}
    </div>
  )
}
